/**
 * R-027: primary PDP fit-first viewer. Stable Component boundary before upgrade.
 * Public coupling: MediaGallery.refs.zoomDialogComponent/open/preloadImage,
 * ZoomMediaSelectedEvent.detail.index, and tcs-pdp-media-zoom readiness.
 * No Horizon method/ref replacement. Failed modules leave native image links.
 */
import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { lockScroll, unlockScroll } from '@theme/utilities';

const clamp = (n, low, high) => Math.max(low, Math.min(high, n));
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const point = (e) => ({ x: e.clientX, y: e.clientY });
const interactive =
  'button, a, input, select, textarea, video, iframe, model-viewer, deferred-media, product-model';

class TcsPdpImageViewer extends Component {
  requiredRefs = ['dialog', 'media', 'stage', 'thumbnails', 'status'];
  ready = false;
  #controller;
  #resize;
  #gallery;
  #sectionId;
  #index = 0;
  #scale = 1;
  #x = 0;
  #y = 0;
  #width = 0;
  #height = 0;
  #loaded = false;
  #openSession = false;
  #opener;
  #scroll;
  #generation = 0;
  #pointers = new Map();
  #gesture;
  #lastTap;
  #highRequests = new WeakMap();
  #detailRequest = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.ready = false;
    this.#gallery = this.closest('media-gallery');
    this.#sectionId = this.closest('.shopify-section')?.id;
    if (!this.#gallery?.hasAttribute('data-tcs-image-viewer') || this.closest('quick-add-dialog'))
      return;
    Promise.all(
      ['media-gallery', 'slideshow-component'].map((name) => customElements.whenDefined(name)),
    ).then(() => {
      if (signal.aborted || !this.isConnected || this.#gallery.refs.zoomDialogComponent !== this)
        return;
      const { dialog, stage } = this.refs;
      this.#gallery.addEventListener('click', this.#openFromGallery, { signal });
      this.#gallery.addEventListener('tcs:variant-image-painted', () => this.#syncPrimaryMedia(), {
        signal,
      });
      this.#syncPrimaryMedia();
      dialog.addEventListener('click', this.#controlClick, { signal });
      // Any key press ends the pointer-open presentation, so keyboard users
      // always get the native :focus-visible ring from then on.
      dialog.addEventListener('keydown', () => dialog.removeAttribute('data-tcs-pointer-open'), {
        capture: true,
        signal,
      });
      dialog.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          this.close();
        },
        { signal },
      );
      dialog.addEventListener('close', () => this.#cleanup(), { signal });
      // B03-F1: a keyboard-focused thumbnail must be fully visible in its
      // scrolling rail (Chromium leaves a partly clipped one where it is). The
      // selected thumbnail is still centred by the selection code below.
      this.refs.thumbnails.addEventListener(
        'focusin',
        (event) =>
          event.target.closest?.('[data-select]')?.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'instant',
          }),
        { signal },
      );
      stage.addEventListener('keydown', this.#keyDown, { signal });
      stage.addEventListener('pointerdown', this.#pointerDown, { signal });
      stage.addEventListener('pointermove', this.#pointerMove, { signal });
      stage.addEventListener('pointerup', this.#pointerUp, { signal });
      stage.addEventListener('pointercancel', () => this.#clearGesture(), { signal });
      stage.addEventListener(
        'lostpointercapture',
        (event) => {
          if (this.#pointers.has(event.pointerId)) this.#clearGesture();
        },
        { signal },
      );
      stage.addEventListener(
        'dragstart',
        (event) => {
          if (this.#isImage()) event.preventDefault();
        },
        { signal },
      );
      window.addEventListener(
        'resize',
        () => {
          if (this.#openSession) this.reset();
        },
        { signal },
      );
      window.visualViewport?.addEventListener(
        'resize',
        () => {
          if (this.#openSession) this.reset();
        },
        { signal },
      );
      this.#resize = new ResizeObserver(() => {
        if (this.#openSession) this.#fit();
      });
      this.#resize.observe(stage);
      this.ready = true;
      this.#gallery.setAttribute('data-tcs-viewer-ready', '');
      this.dispatchEvent(new Event('tcs:viewer-ready'));
    });
  }

  disconnectedCallback() {
    this.close();
    this.ready = false;
    this.#gallery?.removeAttribute('data-tcs-viewer-ready');
    this.#controller?.abort();
    this.#resize?.disconnect();
    super.disconnectedCallback();
  }

  #openFromGallery = (event) => {
    if (!this.ready || this.refs.dialog.contains(event.target)) return;
    const source = event.target.closest('[data-tcs-viewer-open]');
    if (!source || source.closest('media-gallery') !== this.#gallery) return;
    if (event.target.closest(interactive) && !event.target.closest('a[data-tcs-viewer-open]'))
      return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();
    this.open(Number(source.dataset.tcsViewerOpen), event);
  };

  open(index, event) {
    if (!this.ready || this.closest('quick-add-dialog') || !this.refs.media[index]) return;
    event?.preventDefault();
    this.#syncPrimaryMedia();
    if (!this.#openSession) {
      this.#opener =
        event?.target?.closest('button, a') ||
        this.#gallery.querySelector('.tcs-pdp-media-zoom__button');
      this.#scroll = { x: window.scrollX, y: window.scrollY };
      // Browsers mark showModal()'s autofocus as :focus-visible after a mouse or
      // touch activation (Chromium from a non-focusable opener, WebKit from any
      // click). A real pointer click has detail > 0; keyboard activation has 0.
      this.refs.dialog.toggleAttribute(
        'data-tcs-pointer-open',
        event instanceof MouseEvent && event.detail > 0,
      );
      this.refs.dialog.showModal();
      lockScroll(this.refs.dialog);
      this.#openSession = true;
    }
    this.selectThumbnail(index);
  }

  close() {
    if (this.refs.dialog?.open) this.refs.dialog.close();
    this.#cleanup();
  }

  #cleanup() {
    if (!this.#openSession) return;
    this.#openSession = false;
    this.#generation++;
    this.#clearGesture();
    this.#pause(this.refs.media[this.#index]);
    if (!this.#isImage()) {
      this.refs.media[this.#index]
        ?.querySelectorAll('[data-tcs-mounted]')
        .forEach((el) => el.remove());
      this.#loaded = false;
    }
    this.refs.dialog.removeAttribute('data-tcs-pointer-open');
    unlockScroll(this.refs.dialog);
    window.dispatchEvent(new DialogCloseEvent());
    this.#scale = 1;
    this.#x = 0;
    this.#y = 0;
    this.#lastTap = null;
    this.refs.stage.removeAttribute('data-zoomed');
    const scroll = this.#scroll;
    if (scroll) window.scrollTo({ left: scroll.x, top: scroll.y, behavior: 'instant' });
    const opener = this.#opener;
    requestAnimationFrame(() => {
      if (this.#openSession || document.querySelector('dialog[open]')) return;
      const usable = (el) => el?.isConnected && !el.hidden && el.getClientRects().length;
      const owner = this.#sectionId ? document.getElementById(this.#sectionId) : null;
      const candidates = [
        opener,
        this.#gallery?.querySelector('.tcs-pdp-media-zoom__button'),
        owner?.querySelector('media-gallery[data-tcs-image-viewer] .tcs-pdp-media-zoom__button'),
        owner?.querySelector('a.tcs-viewer-fallback'),
      ];
      candidates.find(usable)?.focus({ preventScroll: true });
    });
  }

  // The variant coordinator paints a warmed hero before the full section arrives.
  // Consume its authoritative media descriptor, including when we upgraded late.
  #syncPrimaryMedia() {
    if (this.#openSession) return;
    const hero = this.#gallery.querySelector('slideshow-slide:first-child .product-media__image');
    const id = hero?.closest('[data-media-id]')?.dataset.mediaId;
    const item = this.refs.media[0];
    if (!id || !item || id === item.dataset.mediaId) return;
    let map;
    try {
      map = JSON.parse(this.#gallery.querySelector('[data-tcs-media-map]')?.textContent || 'null');
    } catch {
      return;
    }
    const media = map?.candidates.find((candidate) => String(candidate.image.id) === id)?.image;
    if (!media) return;
    this.#generation++;
    this.#highRequests.delete(item);
    this.#loaded = false;
    item.querySelectorAll('[data-tcs-mounted]').forEach((el) => el.remove());
    item.dataset.mediaId = id;
    item.dataset.mediaType = 'image';
    item.dataset.width = media.width;
    item.dataset.height = media.height;
    item.dataset.highSrc = media.src;
    const image = hero.cloneNode();
    image.className = 'tcs-image-viewer__image';
    for (const attr of ['ref', 'style', 'data-high-resolution', 'data-tcs-mounted'])
      image.removeAttribute(attr);
    image.alt = media.alt || '';
    item.querySelector('template').content.replaceChildren(image);
    const thumbnail = this.refs.thumbnails.querySelector('button img');
    if (thumbnail) {
      thumbnail.removeAttribute('srcset');
      thumbnail.src = hero.currentSrc || hero.src;
    }
    for (const link of this.#gallery.querySelectorAll('a[data-tcs-viewer-open="0"]'))
      link.href = media.src;
  }

  selectThumbnail(index) {
    if (!this.#openSession || !Number.isInteger(index) || !this.refs.media[index]) return;
    if (
      index === this.#index &&
      this.#loaded &&
      this.refs.media[index].querySelector('[data-tcs-mounted]')
    ) {
      this.reset();
      return;
    }
    this.#clearGesture();
    this.#lastTap = null;
    this.#pause(this.refs.media[this.#index]);
    this.#index = index;
    this.#generation++;
    const generation = this.#generation;
    this.#loaded = false;
    this.#scale = 1;
    this.#x = 0;
    this.#y = 0;
    this.refs.media.forEach((item, i) => {
      item.hidden = i !== index;
      item.inert = i !== index;
      item.querySelectorAll('[data-tcs-mounted]').forEach((el) => el.remove());
    });
    const item = this.refs.media[index];
    const template = item.querySelector('template');
    if (!template) return;
    const inertContent = template.content.cloneNode(true);
    const inertImage = inertContent.querySelector('img.tcs-image-viewer__image');
    const originalSources = inertImage
      ? ['sizes', 'srcset', 'src'].map((name) => [name, inertImage.getAttribute(name)])
      : [];
    // Import without image sources so Safari cannot start an unused rendition.
    // Assign the chosen source only in the active document: changing it in the
    // template's inert document can produce a load event with zero dimensions.
    originalSources.forEach(([name]) => inertImage.removeAttribute(name));
    const content = this.ownerDocument.importNode(inertContent, true);
    const fittedImage = content.querySelector('img.tcs-image-viewer__image');
    if (fittedImage) {
      // Reuse this media's already decoded PDP image. A different CDN width here
      // makes the first open wait for a redundant download. Match media identity,
      // never slide position, so variant changes and the grid/carousel pair agree.
      const candidates = [...this.#gallery.querySelectorAll('.product-media__image')].filter(
        (image) =>
          !this.contains(image) &&
          image.closest('[data-media-id]')?.dataset.mediaId === item.dataset.mediaId &&
          image.complete &&
          image.naturalWidth > 0 &&
          image.currentSrc,
      );
      // Desktop grid has a duplicate mobile carousel. Prefer its visible source.
      const source =
        candidates.find((image) => image.getClientRects().length) ||
        candidates.sort((a, b) => b.naturalWidth - a.naturalWidth)[0];
      if (source) {
        fittedImage.src = source.currentSrc;
      } else {
        originalSources.forEach(([name, value]) => {
          if (value !== null) fittedImage.setAttribute(name, value);
        });
      }
    }
    [...content.children].forEach((el) => el.setAttribute('data-tcs-mounted', ''));
    item.appendChild(content);
    if (item.dataset.mediaType === 'model') {
      // The gallery loads ShopifyXR; its initial scan predates our inert template.
      const setup = () => {
        if (this.#openSession && generation === this.#generation && item.isConnected)
          window.ShopifyXR?.setupXRElements();
      };
      if (window.ShopifyXR) setup();
      else
        document.addEventListener('shopify_xr_initialized', setup, {
          once: true,
          signal: this.#controller.signal,
        });
    }
    const buttons = [...this.refs.thumbnails.querySelectorAll('[data-select]')];
    buttons.forEach((b, i) => {
      if (i === index) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
    const thumb = buttons[index];
    if (thumb) {
      const rail = this.refs.thumbnails;
      const offset =
        thumb.offsetLeft - rail.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2;
      rail.scrollTo({ left: Math.max(0, offset), behavior: 'instant' });
    }
    this.refs.count.textContent = this.dataset.count
      .replace('[index]', String(index + 1))
      .replace('[total]', String(this.refs.media.length));
    this.refs.previous.hidden = this.refs.next.hidden = this.refs.media.length < 2;
    this.refs.thumbnails.hidden = this.refs.media.length < 2;
    this.refs.zoomControls.hidden = !this.#isImage();
    this.refs.stage.toggleAttribute('data-image', this.#isImage());
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
    this.#message(this.#isImage() ? 'loading' : null);
    const img = this.#image();
    if (img) {
      const loaded = () => {
        if (generation !== this.#generation || !this.#openSession || img !== this.#image()) return;
        this.#loaded = img.naturalWidth > 0;
        this.#message(this.#loaded ? null : 'error');
        this.#fit();
        this.#announce();
      };
      img.addEventListener('load', loaded, { once: true, signal: this.#controller.signal });
      img.addEventListener('error', loaded, { once: true, signal: this.#controller.signal });
      if (img.complete) loaded();
    } else {
      this.#loaded = true;
      this.#fit();
      this.#announce();
    }
    this.#fit();
  }

  #pause(item) {
    item
      ?.querySelectorAll('deferred-media, product-model')
      .forEach((media) => media.pauseMedia?.());
    item?.querySelectorAll('video').forEach((video) => video.pause());
  }

  #image() {
    return this.refs.media[this.#index]?.querySelector('img.tcs-image-viewer__image');
  }
  #isImage() {
    return this.refs.media[this.#index]?.dataset.mediaType === 'image';
  }
  #maxScale() {
    if (!this.#loaded || !this.#isImage() || !this.#width) return 1;
    const item = this.refs.media[this.#index];
    return clamp(
      Math.min(Number(item.dataset.width), 3840) / (this.#width * window.devicePixelRatio),
      1,
      3,
    );
  }

  #fit() {
    const item = this.refs.media[this.#index];
    const ratio = Number(item.dataset.width) / Number(item.dataset.height) || 1;
    const stage = this.refs.stage;
    this.#width = Math.min(stage.clientWidth, stage.clientHeight * ratio);
    this.#height = this.#width / ratio;
    item.style.setProperty('--tcs-viewer-image-width', `${this.#width}px`);
    item.style.setProperty('--tcs-viewer-image-height', `${this.#height}px`);
    this.#scale = clamp(this.#scale, 1, this.#maxScale());
    this.#render();
    // Resize may sharpen fit, but must not retry a failed deliberate zoom.
    if (this.#scale === 1) this.loadHighResolutionImage(item);
  }

  #bounds() {
    return {
      x: Math.max(0, (this.#width * this.#scale - this.refs.stage.clientWidth) / 2),
      y: Math.max(0, (this.#height * this.#scale - this.refs.stage.clientHeight) / 2),
    };
  }
  #render() {
    const bounds = this.#bounds();
    this.#x = clamp(this.#x, -bounds.x, bounds.x);
    this.#y = clamp(this.#y, -bounds.y, bounds.y);
    const item = this.refs.media[this.#index];
    item.style.setProperty('--tcs-viewer-scale', this.#scale);
    item.style.setProperty('--tcs-viewer-x', `${this.#x}px`);
    item.style.setProperty('--tcs-viewer-y', `${this.#y}px`);
    this.refs.stage.toggleAttribute('data-zoomed', this.#scale > 1);
    this.refs.panControls.hidden = this.#scale <= 1 || !this.#isImage();
    this.refs.zoomIn.disabled = !this.#loaded || this.#scale >= this.#maxScale() - 0.001;
    this.refs.zoomOut.disabled = this.#scale <= 1;
    const zoomPercent = `${Math.round(this.#scale * 100)}%`;
    this.refs.reset.textContent = zoomPercent;
    // WCAG 2.5.3: speech-input users must be able to say the visible label.
    // Keep the changing percentage in the name while retaining the localized
    // action that explains what activating the control does.
    const resetAction = this.refs.reset.dataset.label?.trim();
    this.refs.reset.setAttribute(
      'aria-label',
      resetAction ? `${zoomPercent} — ${resetAction}` : zoomPercent,
    );
    const unavailable = this.#isImage() && this.#loaded && this.#maxScale() <= 1;
    // Keep the same text/space even when unavailable is false: changing the
    // stage height here feeds back into fit/maxScale through ResizeObserver.
    this.refs.zoomNote.style.visibility = unavailable ? 'visible' : 'hidden';
    this.refs.zoomNote.setAttribute('aria-hidden', String(!unavailable));
    for (const [dir, disabled] of Object.entries({
      left: this.#x >= bounds.x - 0.01,
      right: this.#x <= -bounds.x + 0.01,
      up: this.#y >= bounds.y - 0.01,
      down: this.#y <= -bounds.y + 0.01,
    })) {
      const button = this.refs.panControls.querySelector(`[data-action="pan-${dir}"]`);
      if (button) button.disabled = disabled;
    }
  }

  reset() {
    this.#clearGesture();
    this.#scale = 1;
    this.#x = 0;
    this.#y = 0;
    if (this.refs.message.dataset.type?.startsWith('high')) this.#message(null);
    this.#fit();
    this.#announce();
  }

  #zoom(scale, anchor) {
    if (!this.#isImage() || !this.#loaded) return;
    const next = clamp(scale, 1, this.#maxScale());
    const ratio = next / this.#scale;
    this.#x *= ratio;
    this.#y *= ratio;
    if (anchor) {
      this.#x += anchor.x * (1 - ratio);
      this.#y += anchor.y * (1 - ratio);
    }
    this.#scale = next;
    if (next === 1) {
      this.#x = this.#y = 0;
      if (this.refs.message.dataset.type?.startsWith('high')) this.#message(null);
    }
    if (next > 1) this.loadHighResolutionImage(this.refs.media[this.#index]);
    if (next === 1) this.#fit();
    else this.#render();
  }

  loadHighResolutionImage(item) {
    if (
      !this.#openSession ||
      item !== this.refs.media[this.#index] ||
      !this.#isImage() ||
      !this.#loaded
    )
      return;
    const img = this.#image();
    if (!img || img.dataset.highResolution) return;
    const forFit = this.#scale <= 1;
    const maximum = Math.min(Number(item.dataset.width), 3840);
    const needed = Math.min(Math.ceil(this.#width * window.devicePixelRatio), maximum);
    if (forFit && Math.max(img.naturalWidth, Number(img.dataset.fitResolution) || 0) >= needed)
      return;
    // Keep the decoded image visible while a small source sharpens. Bucket CDN
    // sizes so repeated resizes cannot create a request for every pixel width.
    const targetWidth = Math.min(
      [480, 800, 1200, 1600, 1920, 2560, 3840].find((width) => width >= needed) || 3840,
      maximum,
    );
    const url = new URL(item.dataset.highSrc, document.baseURI);
    if (forFit) url.searchParams.set('width', String(targetWidth));
    const generation = this.#generation;
    const detailRequest = ++this.#detailRequest;
    let requests = this.#highRequests.get(item);
    if (!requests) this.#highRequests.set(item, (requests = new Map()));
    let request = requests.get(url.href);
    if (!request) {
      request = new Promise((resolve) => {
        const high = new Image();
        high.onload = () => resolve(high.src);
        high.onerror = () => resolve(null);
        high.src = url.href;
      });
      requests.set(url.href, request);
    }
    // Initial fit is already usable; do not flash a loading banner over it.
    this.#message(forFit ? null : 'highLoading');
    this.#announce();
    request.then((src) => {
      if (!src && requests.get(url.href) === request) requests.delete(url.href);
      if (
        !this.#openSession ||
        generation !== this.#generation ||
        detailRequest !== this.#detailRequest ||
        img !== this.#image() ||
        (!forFit && this.#scale <= 1)
      )
        return;
      if (!src) {
        this.#message('highError');
        this.#announce();
        return;
      }
      this.#message(null);
      img.removeAttribute('srcset');
      img.src = src;
      if (forFit) img.dataset.fitResolution = String(targetWidth);
      else img.dataset.highResolution = 'true';
      this.#announce();
    });
  }

  #message(type) {
    this.refs.message.hidden = !type;
    this.refs.message.dataset.type = type || '';
    this.refs.retry.hidden = type !== 'error' && type !== 'highError';
    this.refs.messageText.textContent = type ? this.dataset[type] : '';
  }
  #announce() {
    if (!this.#openSession) return;
    const state = this.#isImage()
      ? this.dataset.zoomState.replace('[scale]', String(Math.round(this.#scale * 100)))
      : '';
    this.refs.status.textContent = `${this.refs.count.textContent}. ${this.#loaded ? `${state} ${this.refs.zoomNote.getAttribute('aria-hidden') === 'true' ? '' : this.dataset.zoomUnavailable} ${this.refs.messageText.textContent}` : this.refs.messageText.textContent}`;
  }
  #navigate(delta) {
    const count = this.refs.media.length;
    const next = this.#gallery.slideshow?.hasAttribute('infinite')
      ? (this.#index + delta + count) % count
      : clamp(this.#index + delta, 0, count - 1);
    this.selectThumbnail(next);
  }
  #pan(direction) {
    const step = Math.min(this.refs.stage.clientWidth, this.refs.stage.clientHeight) / 5;
    this.#x += direction === 'left' ? step : direction === 'right' ? -step : 0;
    this.#y += direction === 'up' ? step : direction === 'down' ? -step : 0;
    this.#render();
  }
  #controlClick = (event) => {
    const button = event.target.closest('button');
    if (!button || button.disabled) return;
    if (button.hasAttribute('data-select')) {
      this.selectThumbnail(Number(button.dataset.select));
      return;
    }
    const action = button.dataset.action;
    if (action === 'close') this.close();
    else if (action === 'previous') this.#navigate(-1);
    else if (action === 'next') this.#navigate(1);
    else if (action === 'zoom-in' || action === 'zoom-out') {
      this.#zoom(this.#scale + (action === 'zoom-in' ? 0.5 : -0.5));
      this.#announce();
    } else if (action === 'reset') this.reset();
    else if (action === 'retry') {
      if (this.refs.message.dataset.type === 'highError')
        this.loadHighResolutionImage(this.refs.media[this.#index]);
      else this.selectThumbnail(this.#index);
    } else if (action?.startsWith('pan-')) this.#pan(action.slice(4));
  };
  #keyDown = (event) => {
    if (
      event.target !== this.refs.stage ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      !this.#isImage()
    )
      return;
    const dir = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' }[
      event.key
    ];
    if (!dir) return;
    if (this.#scale > 1) {
      event.preventDefault();
      this.#pan(dir);
    } else if (dir === 'left' || dir === 'right') {
      event.preventDefault();
      this.#navigate(dir === 'left' ? -1 : 1);
    }
  };

  #clearGesture() {
    const ids = [...this.#pointers.keys()];
    this.#pointers.clear();
    this.#gesture = null;
    this.refs.stage?.removeAttribute('data-dragging');
    for (const id of ids) {
      if (this.refs.stage?.hasPointerCapture(id)) this.refs.stage.releasePointerCapture(id);
    }
  }
  #pointerDown = (event) => {
    if (
      !this.#isImage() ||
      !this.#loaded ||
      event.button !== 0 ||
      event.target.closest(interactive)
    )
      return;
    const p = point(event);
    this.#pointers.set(event.pointerId, p);
    this.refs.stage.setPointerCapture(event.pointerId);
    if (this.#pointers.size === 1) {
      this.#gesture = {
        start: p,
        x: this.#x,
        y: this.#y,
        scale: this.#scale,
        time: performance.now(),
        moved: false,
        multi: false,
      };
    } else if (this.#pointers.size === 2) {
      const [a, b] = [...this.#pointers.values()];
      this.#gesture = { scale: this.#scale, distance: distance(a, b), multi: true, moved: true };
      this.#lastTap = null;
    } else this.#clearGesture();
  };
  #pointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId) || !this.#gesture) return;
    const p = point(event);
    this.#pointers.set(event.pointerId, p);
    const gesture = this.#gesture;
    if (this.#pointers.size === 2) {
      const [a, b] = [...this.#pointers.values()];
      const rect = this.refs.stage.getBoundingClientRect();
      this.#zoom((gesture.scale * distance(a, b)) / Math.max(gesture.distance, 1), {
        x: (a.x + b.x) / 2 - rect.left - rect.width / 2,
        y: (a.y + b.y) / 2 - rect.top - rect.height / 2,
      });
    } else if (!gesture.multi) {
      const dx = p.x - gesture.start.x,
        dy = p.y - gesture.start.y;
      if (Math.hypot(dx, dy) > 8) gesture.moved = true;
      if (gesture.scale > 1) {
        this.#x = gesture.x + dx;
        this.#y = gesture.y + dy;
        this.#render();
      }
    }
    this.refs.stage.toggleAttribute('data-dragging', gesture.moved);
  };
  #pointerUp = (event) => {
    if (!this.#pointers.has(event.pointerId) || !this.#gesture) return;
    const gesture = this.#gesture;
    const end = point(event);
    this.#clearGesture();
    if (gesture.multi) {
      this.#announce();
      return;
    }
    const elapsed = performance.now() - gesture.time;
    const dx = end.x - gesture.start.x,
      dy = end.y - gesture.start.y;
    if (
      gesture.scale === 1 &&
      Math.abs(dx) > 48 &&
      Math.abs(dx) > Math.abs(dy) * 1.3 &&
      elapsed < 700
    ) {
      this.#navigate(dx > 0 ? -1 : 1);
      this.#lastTap = null;
      return;
    }
    if (!gesture.moved && elapsed < 300 && event.pointerType !== 'mouse') {
      const now = performance.now();
      if (this.#lastTap && now - this.#lastTap.time < 300 && distance(end, this.#lastTap) < 32) {
        this.#zoom(this.#scale > 1 ? 1 : 2);
        this.#announce();
        this.#lastTap = null;
      } else this.#lastTap = { ...end, time: now };
    } else this.#lastTap = null;
  };
}
if (!customElements.get('tcs-pdp-image-viewer-component'))
  customElements.define('tcs-pdp-image-viewer-component', TcsPdpImageViewer);
